// Tipos e formatação do Estoque. Quantidade chega como texto decimal com 3 casas ("12.500"); dinheiro em centavos
// (texto). Nenhum cálculo de quantidade ou dinheiro passa por float.

export type TipoMov = 'entrada' | 'saida' | 'transferencia' | 'ajuste';
export const TIPOS_MOV: TipoMov[] = ['entrada', 'saida', 'transferencia', 'ajuste'];

export interface Unidade {
  id: string;
  codigo: string;
  nome: string;
  fracionavel: boolean;
  produtos: number;
}

export interface ProdutoResumo {
  id: string;
  codigo: string;
  nome: string;
  unidade: string;
  codigo_barras: string | null;
  custo_centavos: string;
  preco_centavos: string;
  moeda: string;
  estoque_minimo: string;
  controla_lote: boolean;
  ativo: boolean;
  saldo: string;
  abaixo_minimo: boolean;
  variacoes: number;
  extras: Record<string, unknown>;
  custo_medio_centavos: string | null;
  valor_centavos: string;
}

export interface Variacao {
  id: string;
  produto_id: string;
  produto: string;
  produto_codigo: string;
  unidade: string;
  codigo: string;
  nome: string;
  atributos: Record<string, string>;
  codigo_barras: string | null;
  preco_centavos: string;
  estoque_minimo: string;
  ativo: boolean;
  saldo: string;
}

export interface FichaProduto {
  id: string;
  codigo: string;
  nome: string;
  descricao: string | null;
  unidade: string;
  unidade_nome: string | null;
  fracionavel: boolean;
  codigo_barras: string | null;
  custo_centavos: string;
  preco_centavos: string;
  moeda: string;
  estoque_minimo: string;
  controla_lote: boolean;
  atributos: Record<string, string[]>;
  fornecedor_id: string | null;
  fornecedor: string | null;
  ativo: boolean;
  /** campos personalizados de estoque.produto (N8) */
  extras?: Record<string, unknown>;
  saldo: string;
  custo_medio_centavos: string | null;
  valor_centavos: string;
  variacoes: { id: string; codigo: string; nome: string; atributos: Record<string, string>; codigo_barras: string | null; preco_centavos: string | null; estoque_minimo: string | null; ativo: boolean; saldo: string; custo_medio_centavos: string | null }[];
  /** M9.7: custo_medio_centavos é o da empresa do depósito (sem empresa = grupo). */
  saldos: { deposito_id: string; deposito: string; empresa_id: string | null; variacao_id: string | null; variacao: string | null; lote_id: string | null; lote: string | null; validade: string | null; quantidade: string; custo_medio_centavos: string | null }[];
  /** M9.7: custo médio, saldo e valor por empresa (empresa nula = grupo) e variação, só o que a sessão vê. */
  custos: { empresa_id: string | null; empresa: string | null; variacao_id: string | null; variacao: string | null; custo_medio_centavos: string; saldo: string; valor_centavos: string }[];
  movimentacoes: { id: string; numero: string; tipo: TipoMov; data: string; quantidade: string; valor_centavos: string; motivo: string | null; deposito: string; deposito_destino: string | null; variacao: string | null; lote: string | null; origem: string }[];
}

export interface Deposito {
  id: string;
  codigo: string;
  nome: string;
  endereco: string | null;
  ativo: boolean;
  empresa_id: string | null;
  empresa: string | null;
  posicoes: number;
  itens: number;
  valor_centavos: string;
  locais: { id: string; codigo: string; descricao: string | null }[];
}

export interface Movimentacao {
  id: string;
  numero: string;
  tipo: TipoMov;
  data: string;
  produto_id: string;
  produto: string;
  produto_codigo: string;
  unidade: string;
  variacao: string | null;
  lote: string | null;
  deposito: string;
  deposito_destino: string | null;
  quantidade: string;
  custo_unitario_centavos: string;
  valor_centavos: string;
  moeda: string;
  motivo: string | null;
  origem: string;
  origem_ref: string | null;
}

export interface ItemReposicao {
  produto_id: string;
  variacao_id: string | null;
  codigo: string;
  nome: string;
  unidade: string;
  minimo: string;
  saldo: string;
  falta: string;
  fornecedor: string | null;
  ultimo_alerta: string | null;
}

export interface Alerta {
  id: string;
  dia: string;
  produto_id: string;
  produto: string;
  produto_codigo: string;
  variacao: string | null;
  saldo: string;
  minimo: string;
}

export interface Inventario {
  id: string;
  numero: string;
  deposito_id: string;
  deposito: string;
  descricao: string | null;
  estado: 'aberto' | 'fechado' | 'cancelado';
  aberto_em: string;
  fechado_em: string | null;
  ajustes: number;
  itens: number;
  contados: number;
}

export interface ItemInventario {
  id: string;
  produto_id: string;
  codigo: string;
  produto: string;
  unidade: string;
  fracionavel: boolean;
  variacao_id: string | null;
  variacao: string | null;
  lote_id: string | null;
  lote: string | null;
  validade: string | null;
  saldo_sistema: string;
  /** saldo do sistema quando o contado foi lançado (base da diferença, SM2-R3-03) */
  saldo_contagem: string | null;
  saldo_atual: string;
  contado: string | null;
  diferenca: string | null;
  ajuste: string | null;
}

export interface FichaInventario extends Omit<Inventario, 'itens' | 'contados'> {
  itens: ItemInventario[];
}

export interface Lote {
  id: string;
  codigo: string;
  validade: string | null;
  fabricacao: string | null;
  produto_id: string;
  produto: string;
  produto_codigo: string;
  unidade: string;
  variacao: string | null;
  saldo: string;
  dias_para_vencer: number | null;
  depositos: { deposito: string; quantidade: string }[];
}

export interface Sugestao {
  suficiente: boolean;
  lotes: { lote_id: string; codigo: string; validade: string | null; saldo: string; usar: string }[];
  /** SM2-06: lotes vencidos com saldo, fora da sugestão; a saída deles pede confirmação. */
  vencidos?: { lote_id: string; codigo: string; validade: string | null; saldo: string }[];
}

export function formatarDinheiro(centavos: string | null | undefined, moeda = 'BRL', idioma = 'pt-BR'): string {
  if (centavos === null || centavos === undefined || centavos === '') return '';
  const c = BigInt(centavos);
  const neg = c < 0n;
  const a = neg ? -c : c;
  const decimal = `${neg ? '-' : ''}${(a / 100n).toString()}.${(a % 100n).toString().padStart(2, '0')}`;
  return new Intl.NumberFormat(idioma, { style: 'currency', currency: moeda }).format(decimal as unknown as number);
}

const separador = (idioma: string) => new Intl.NumberFormat(idioma).formatToParts(1.5).find((p) => p.type === 'decimal')?.value ?? ',';

/** "1250.500" vira "1.250,5" em pt, sem float (a parte inteira é formatada como bigint). */
export function formatarQtd(t: string | null | undefined, idioma: string): string {
  if (t === null || t === undefined || t === '') return '';
  const neg = t.startsWith('-');
  const [i = '0', f = ''] = t.replace('-', '').split('.');
  const dec = f.replace(/0+$/, '');
  return `${neg ? '-' : ''}${new Intl.NumberFormat(idioma).format(BigInt(i))}${dec ? `${separador(idioma)}${dec}` : ''}`;
}

/** Texto digitado ("1,5", "1.5", "-2") para o formato da API ("1.5"); null se vazio, 'invalido' se não é decimal com até 3 casas. */
export function qtdParaApi(texto: string, negativo = false): string | null {
  const t = texto.trim().replace(/\s/g, '');
  if (!t) return null;
  const n = t.includes(',') ? t.replace(/\./g, '').replace(',', '.') : t;
  const re = negativo ? /^-?\d{1,15}(\.\d{1,3})?$/ : /^\d{1,15}(\.\d{1,3})?$/;
  return re.test(n) ? n : 'invalido';
}

/** "1.500" para o campo de edição ("1,5" em pt). */
export const qtdParaCampo = (t: string | null | undefined): string => (!t ? '' : t.includes('.') ? t.replace(/0+$/, '').replace(/\.$/, '').replace('.', ',') : t);

export function reaisParaCentavos(texto: string): string | null {
  const t = texto.trim().replace(/\s|R\$/g, '');
  if (!t) return null;
  const m = /^(\d{1,3}(?:\.\d{3})*|\d+)(?:,(\d{1,2}))?$/.exec(t);
  if (!m) return 'invalido';
  return (BigInt(m[1]!.replace(/\./g, '')) * 100n + BigInt((m[2] ?? '').padEnd(2, '0') || '0')).toString();
}

export function centavosParaCampo(c: string | null | undefined): string {
  if (!c) return '';
  const v = BigInt(c);
  return `${(v / 100n).toString()},${(v % 100n).toString().padStart(2, '0')}`;
}

// Data civil (coluna date, AAAA-MM-DD: data da movimentação, validade, dia do alerta): o dia escrito, sem fuso.
export const formatarData = (iso: string | null | undefined, idioma: string) => (iso ? new Intl.DateTimeFormat(idioma, { timeZone: 'UTC' }).format(new Date(`${iso.slice(0, 10)}T00:00:00Z`)) : '');
// Instante (timestamptz: aberto_em, fechado_em, created_at): o dia no fuso do navegador (VB11-01).
// Cortar o ISO em AAAA-MM-DD pegaria o dia UTC, que depois das 21h de Brasília já é o dia seguinte.
export const formatarInstante = (iso: string | null | undefined, idioma: string) => (iso ? new Intl.DateTimeFormat(idioma, { dateStyle: 'short' }).format(new Date(iso)) : '');
export const formatarDataHora = (iso: string | null | undefined, idioma: string) => (iso ? new Intl.DateTimeFormat(idioma, { dateStyle: 'short', timeStyle: 'short' }).format(new Date(iso)) : '');

export const hojeIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** Diferença exata entre duas quantidades em texto (a - b), em texto com 3 casas. */
export function subtrairQtd(a: string, b: string): string {
  const mil = (t: string) => {
    const neg = t.startsWith('-');
    const [i = '0', f = ''] = t.replace('-', '').split('.');
    const v = BigInt(i) * 1000n + BigInt(f.padEnd(3, '0').slice(0, 3));
    return neg ? -v : v;
  };
  const d = mil(a) - mil(b);
  const neg = d < 0n;
  const x = neg ? -d : d;
  return `${neg ? '-' : ''}${(x / 1000n).toString()}.${(x % 1000n).toString().padStart(3, '0')}`;
}
